import { takeEvery, call, put } from 'redux-saga/effects'
import { AxiosResponse } from 'axios'
//add REPORTING_ENDPOINTS
import * as api from 'services/api'
import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { BenchmarkData, BenchmarkInfo, BenchmarkReportRequest, Whale } from 'store/reporting/benchmark/types'
import { blobToFile } from 'store/utils'

type loadingTypes = {
  benchmarks: boolean
  whales: boolean //loading for fetching benchmark data, which is currently only whales but could be more in the future. If so, rename to data
}

type errorTypes = {
  createBenchmark: string | null
  benchmarks: string | null
  fetchStatus: string | null
}

type LoadingKeyValueType = {
  key: keyof loadingTypes
  value: boolean
}
type ErrorKeyValueType = {
  key: keyof errorTypes
  value: null | string
}
type BenchmarkReportState = {
  benchmarkId: string
  benchmarkInfo?: BenchmarkInfo
  status: string
  whales: Whale[] | null
  benchmarks: BenchmarkInfo[] | null
  loading: loadingTypes
  error: errorTypes
}

const initialState: BenchmarkReportState = {
  benchmarkId: '',
  status: '',
  whales: null,
  benchmarks: null,
  loading: {
    benchmarks: false,
    whales: false,
  },
  error: {
    createBenchmark: null,
    benchmarks: null,
    fetchStatus: null,
  },
}

const slice = createSlice({
  name: 'benchmark',
  initialState,
  reducers: {
    createBenchmark: (state, action: PayloadAction<BenchmarkReportRequest>) => {},
    setCreateBenchmarkResponse: (state, action: PayloadAction<string>) => {
      state.benchmarkId = action.payload
    },
    fetchBenchmarkInfo: (state, action: PayloadAction<string>) => {},
    setBenchmarkInfo: (state, action: PayloadAction<BenchmarkInfo>) => {
      state.benchmarkInfo = action.payload
    },
    fetchBenchmarkStatus: (state, action: PayloadAction<string>) => {},
    setStatus: (state, action: PayloadAction<string>) => {
      state.status = action.payload
    },
    fetchBenchmarkData: (state, action: PayloadAction<string>) => {},
    setBenchmarkData: (state, action: PayloadAction<Whale[]>) => {
      state.whales = action.payload
    },
    downloadBenchmarkZip: (state, action: PayloadAction<string>) => {},
    fetchBenchmarks: (state, action: PayloadAction<string>) => {},
    setBenchmarks: (state, action: PayloadAction<BenchmarkInfo[]>) => {
      state.benchmarks = action.payload
    },
    setLoading: (state, action: PayloadAction<LoadingKeyValueType>) => {
      const { key, value } = action.payload
      state.loading[key] = value
    },
    setError: (state, action: PayloadAction<ErrorKeyValueType>) => {
      const { key, value } = action.payload
      state.error[key] = value
    },
  },
})

export const actions = slice.actions

export const reducer = slice.reducer

export function* sagas() {
  yield takeEvery(actions.createBenchmark, createBenchmarkSaga)
  yield takeEvery(actions.fetchBenchmarkInfo, fetchBenchmarkInfoSaga)
  yield takeEvery(actions.fetchBenchmarkStatus, fetchBenchmarkStatusSaga)
  yield takeEvery(actions.fetchBenchmarkData, fetchBenchmarkDataSaga)
  yield takeEvery(actions.downloadBenchmarkZip, downloadBenchmarkZipSaga)
  yield takeEvery(actions.fetchBenchmarks, fetchBenchmarksSaga)
}

/**
 * Sagas
 */
export const createBenchmarkSaga = function* (action: PayloadAction<BenchmarkReportRequest>) {
  try {
    yield put(actions.setError({ key: 'createBenchmark', value: null }))
    const response: AxiosResponse<{ id: string }> = yield call(api.authPost, api.REPORTING_ENDPOINTS.benchmark, action.payload)
    yield put(actions.setCreateBenchmarkResponse(response.data.id))
  } catch (error: any) {
    yield put(actions.setError({ key: 'createBenchmark', value: error?.message }))

    console.log(error)
  }
}

export const fetchBenchmarkInfoSaga = function* (action: PayloadAction<string>) {
  try {
    const response: AxiosResponse<BenchmarkInfo> = yield call(
      api.authGet,
      api.REPORTING_ENDPOINTS.benchmarkInfo(action.payload),
      {},
    )
    yield put(actions.setBenchmarkInfo(response.data))
  } catch (error: any) {
    console.log(error)
  }
}

export const fetchBenchmarkStatusSaga = function* (action: PayloadAction<string>) {
  try {
    yield put(actions.setError({ key: 'fetchStatus', value: null }))
    const response: AxiosResponse<{ status: string }> = yield call(
      api.authGet,
      api.REPORTING_ENDPOINTS.benchmarkStatus(action.payload),
      {},
    )
    yield put(actions.setStatus(response.data.status))
  } catch (error: any) {
    yield put(actions.setError({ key: 'fetchStatus', value: error?.message }))

    console.log(error)
  }
}

//If this is going to be only used to fetch whales, maybe we can rename it to getWhalesSaga and the endpoint to benchmarkWhales or something like that, to be more specific.
export const fetchBenchmarkDataSaga = function* (action: PayloadAction<string>) {
  try {
    yield put(actions.setLoading({ key: 'whales', value: true }))
    const response: AxiosResponse<Whale[]> = yield call(
      api.authPost,
      api.REPORTING_ENDPOINTS.benchmarkData(action.payload),
      action.payload,
    )
    yield put(actions.setBenchmarkData(response.data))
    yield put(actions.setLoading({ key: 'whales', value: false }))
  } catch (error: any) {
    console.log(error)
  }
}

export const fetchBenchmarksSaga = function* (action: PayloadAction<string>) {
  try {
    yield put(actions.setLoading({ key: 'benchmarks', value: true }))
    const response: AxiosResponse<BenchmarkInfo[]> = yield call(api.authGet, api.REPORTING_ENDPOINTS.benchmark, {})
    yield put(actions.setBenchmarks(response.data))
    yield put(actions.setLoading({ key: 'benchmarks', value: false }))
  } catch (error: any) {
    console.log(error)
    yield put(actions.setLoading({ key: 'benchmarks', value: false }))
    yield put(actions.setError({ key: 'benchmarks', value: error?.message }))
  }
}

export const downloadBenchmarkZipSaga = function* (action: PayloadAction<string>) {
  try {
    const response: AxiosResponse<BenchmarkData> = yield call(
      api.authPostBlob,
      api.REPORTING_ENDPOINTS.benchmarkZip(action.payload),
      action.payload,
    )

    blobToFile(response.data, 'benchmark', 'zip')
  } catch (error: any) {
    console.log(error)
  }
}
